import {
  ReturnTagSource,
  Tag,
  TagKind,
} from "@redotech/redo-common-model-tags-common/tag";
import { PillTheme } from "@redotech/redo-storefront-common/pill-theme";

export const SHOPIFY_ZERO_DOLLAR_MANAGED_MARKETS_EXCHANGE_FAILURE_TYPE =
  "SHOPIFY_ZERO_DOLLAR_MANAGED_MARKETS_EXCHANGE";

export const SHOPIFY_ZERO_DOLLAR_MANAGED_MARKETS_EXCHANGE_MESSAGE =
  "Redo can't create this exchange order because Shopify doesn't allow zero-dollar exchange orders in Managed Markets. You have to resolve this manually by creating a new order for the customer and charging them for order shipping.";

export enum SystemReturnTagName {
  RMA_CREATION_FAILED = "RMA creation failed",
  LABEL_CREATION_FAILED = "Label creation failed",
  EXCHANGE_OUT_OF_STOCK = "Out of Stock",
  LABEL_EXPIRATION_UNSUCCESSFUL = "Label expiration unsuccessful",
  REFUND_PAYMENT_FAILED = "Refund payment failed",
  EXCHANGE_DRAFT_ORDER_CREATION_FAILED = "Exchange draft order failed",
  RESTOCK_FAILED = "Restock failed",
  TRACKING_RETURN_TO_SENDER = "Returning to sender",
  TRACKING_DELAYED_IN_CUSTOMS = "Delayed in customs",
  TRACKING_ERROR_OTHER = "Shipment Error",
  FRAUD_RISK = "Fraud risk",
  SHOPIFY_EXCHANGE_PAYMENT_RECORD_FAILED = "Shopify payment record failed",
  CUSTOMS_INFO_INCOMPLETE = "Customs info incomplete",
}

export const RmaCreationFailedTag = createReturnTag(
  SystemReturnTagName.RMA_CREATION_FAILED,
  PillTheme.DANGER,
);
export const LabelCreationFailedTag = createReturnTag(
  SystemReturnTagName.LABEL_CREATION_FAILED,
  PillTheme.SOLID_ORANGE,
);
export const OutOfStockTag = createReturnTag(
  SystemReturnTagName.EXCHANGE_OUT_OF_STOCK,
  PillTheme.NEUTRAL_YELLOW,
);

export const LabelExpirationUnsuccessfulTag = createReturnTag(
  SystemReturnTagName.LABEL_EXPIRATION_UNSUCCESSFUL,
  PillTheme.DANGER,
);

export const RefundPaymentFailedTag = createReturnTag(
  SystemReturnTagName.REFUND_PAYMENT_FAILED,
  PillTheme.SOLID_ORANGE,
);

export const ExchangeDraftOrderCreationFailedTag = createReturnTag(
  SystemReturnTagName.EXCHANGE_DRAFT_ORDER_CREATION_FAILED,
  PillTheme.SOLID_ORANGE,
);

export const RestockFailedTag = createReturnTag(
  SystemReturnTagName.RESTOCK_FAILED,
  PillTheme.SOLID_ORANGE,
);

export const ShopifyExchangePaymentRecordFailedTag = createReturnTag(
  SystemReturnTagName.SHOPIFY_EXCHANGE_PAYMENT_RECORD_FAILED,
  PillTheme.SOLID_ORANGE,
);

export const TrackingReturnToSenderTag = createReturnTag(
  SystemReturnTagName.TRACKING_RETURN_TO_SENDER,
  PillTheme.SOLID_ORANGE,
);

export const TrackingDelayedInCustomsTag = createReturnTag(
  SystemReturnTagName.TRACKING_DELAYED_IN_CUSTOMS,
  PillTheme.NEUTRAL_YELLOW,
);

export const TrackingErrorOtherTag = createReturnTag(
  SystemReturnTagName.TRACKING_ERROR_OTHER,
  PillTheme.SOLID_ORANGE,
);

export const CustomsInfoIncompleteTag = createReturnTag(
  SystemReturnTagName.CUSTOMS_INFO_INCOMPLETE,
  PillTheme.NEUTRAL_YELLOW,
);

export const FraudRiskTag = createReturnTag(
  SystemReturnTagName.FRAUD_RISK,
  PillTheme.SOLID_ORANGE,
);

export const TRACKING_EXCEPTION_TAG_NAMES = [
  SystemReturnTagName.TRACKING_RETURN_TO_SENDER,
  SystemReturnTagName.TRACKING_DELAYED_IN_CUSTOMS,
  SystemReturnTagName.TRACKING_ERROR_OTHER,
] as const;

export type TrackingExceptionTagName =
  (typeof TRACKING_EXCEPTION_TAG_NAMES)[number];

export const TRACKING_EXCEPTION_TAGS: ReturnTag[] = [
  TrackingReturnToSenderTag,
  TrackingDelayedInCustomsTag,
  TrackingErrorOtherTag,
];

/**
 * Merchant-facing remediation copy keyed by tracking-exception tag name.
 * Shown alongside the tag wherever the merchant encounters a flagged return.
 */
export const TRACKING_EXCEPTION_REMEDIATION: Record<
  TrackingExceptionTagName,
  string
> = {
  [SystemReturnTagName.TRACKING_RETURN_TO_SENDER]:
    "The carrier is returning this package to the customer. Contact the customer to confirm their address and reship, or issue a refund.",
  [SystemReturnTagName.TRACKING_DELAYED_IN_CUSTOMS]:
    "This package is held up in customs. No action needed yet — if it stays delayed for more than 7 days, contact the carrier.",
  [SystemReturnTagName.TRACKING_ERROR_OTHER]:
    "The carrier reported a tracking problem. Open the tracking page for details.",
};

export interface ReturnTag extends Tag {
  name: SystemReturnTagName;
  pillTheme: PillTheme;
  source: ReturnTagSource.REDO;
  kind: TagKind.RETURN;
}

export function createReturnTag(
  name: SystemReturnTagName,
  pillTheme: PillTheme,
): ReturnTag {
  return {
    name,
    pillTheme,
    source: ReturnTagSource.REDO,
    kind: TagKind.RETURN,
  };
}
